import React, { FC, useContext } from 'react';

import { LucidTheme, mqMin, Products } from '@lucid/theme';

import ABTest, { ABTestVariation } from '../../ab-test/ab-test';
import ABTestBranch from '../../ab-test-branch/ab-test-branch';
import AlertBanner from '../alert/alert-banner';
import Container from '../../container/container';
import LayoutContext from '../../context/layout-context';
import { LinkProps } from '../../link/link';

type Announcement = {
  linkText: string,
  linkUrl: string,
  storageKey: string,
  content: string
};

export interface AnnouncementBannerProps {
  /** Changes the default branch to display the banner by default (for snapshot testing) */
  isTestMode?: boolean;
  /** The specific site. */
  product: Products;
}

const AnnouncementBanner: FC<AnnouncementBannerProps> = ({
  isTestMode = false,
  product
}) => {
  const {
    language: { langPrefix },
    location: { pathname }
  } = useContext(LayoutContext);

  const announcements: Partial<Record<Products | 'default', Announcement>> = {
    default: {
      linkText: 'Link to somewhere',
      linkUrl: 'https://www.google.com/',
      storageKey: 'important-announcement',
      content: 'This is placeholder text for a really important announcement right here.'
    },
    chart: {
      linkText: 'Link to somewhere',
      linkUrl: 'https://www.google.com/',
      storageKey: 'chart-only-announcement',
      content: 'Some other announcement to overwrite the default one on chart only.'
    },
  };
  const announcement = announcements[product] || announcements.default || null;

  if (!announcement || langPrefix !== 'en' || pathname.includes('/landing')) {
    return null;
  }

  const { linkText, linkUrl, storageKey, content } = announcement;

  const linkProps: LinkProps = {
    hasArrow: true,
    underline: 'none',
    label: linkText,
    to: linkUrl
  };

  const announcementBanner = (
    <Container
      css={(theme: LucidTheme) => ({
        padding: 0,
        [mqMin.tablet]: {
          paddingTop: theme.space.s16,
          paddingBottom: theme.space.s8,
          '&:empty': {
            padding: 0,
          }
        }
      })}
    >
      <AlertBanner
        linkProps={linkProps}
        storageKey={storageKey}
        storageType="local"
        variant="announcement"
        willDismissOnLinkClick={false}
      >
        {content}
      </AlertBanner>
    </Container>
  );

  if(isTestMode) {
    return announcementBanner;
  }

  return (
    <ABTest
      isAboveTheFold
      css={(theme: LucidTheme) => ({
        zIndex: theme.zIndices.z500,
        [mqMin.tabletLandscape]: {
          position: 'sticky',
          top: '70px',
        }
      })}
      test="Y22Q1_AnnouncementBanner"
    >
      <ABTestBranch
        branch={ABTestVariation.A}
        css={{ display: 'none' }}
      >
        <></>
      </ABTestBranch>
      <ABTestBranch branch={ABTestVariation.B}>
        {announcementBanner}
      </ABTestBranch>
    </ABTest>
  );
};

export { AnnouncementBanner };
export default AnnouncementBanner;
